import React from "react";
import { Toast, ToastContainer } from "react-bootstrap";

// ============================================
// UTILITAIRES PARTAGÉS - GESTION GSM
// ============================================
const IPV4_REGEX = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

export const isValidIp = (ip) => IPV4_REGEX.test((ip || '').trim());

// IP ou nom d'hôte (ex. accès distant via DNS dynamique)
const HOSTNAME_REGEX = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;

export const isValidHost = (host) => isValidIp(host) || HOSTNAME_REGEX.test((host || '').trim());

export const isValidUrl = (value) => {
  try {
    const url = new URL((value || '').trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

export const formatDate = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const NOTIF_BG = { success: 'success', error: 'danger', warning: 'warning', info: 'info' };
const NOTIF_TITLE = { success: 'Succès', error: 'Erreur', warning: 'Attention', info: 'Information' };

export function useNotifications() {
  const [notifications, setNotifications] = React.useState([]);

  const hideNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [{ id, message, type }, ...prev]);
    setTimeout(() => hideNotification(id), duration);
  };

  return { notifications, showNotification, hideNotification };
}

export function GsmToasts({ notifications, onClose }) {
  return (
    <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
      {notifications.map((n) => {
        const bg = NOTIF_BG[n.type] || 'primary';
        return (
          <Toast key={n.id} onClose={() => onClose(n.id)} bg={bg}>
            <Toast.Header className={`bg-${bg} text-white`}>
              <strong className="me-auto">{NOTIF_TITLE[n.type] || 'Information'}</strong>
            </Toast.Header>
            <Toast.Body className="text-white">{n.message}</Toast.Body>
          </Toast>
        );
      })}
    </ToastContainer>
  );
}

export function GsmStyles() {
  return (
    <style>{`
      .gsm-page { background: #f8fafc; min-height: 100vh; }

      /* Header */
      .gsm-header {
        background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
        border-radius: 16px; padding: 24px 28px;
        border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
      }
      .gsm-header-icon {
        width: 56px; height: 56px; border-radius: 14px;
        background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
        color: white; display: flex; align-items: center; justify-content: center;
        font-size: 26px; box-shadow: 0 8px 20px -6px rgba(14, 165, 233, 0.5);
      }
      .gsm-header-title { font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em; }
      .gsm-header-subtitle { color: #64748b; font-size: 0.875rem; }

      /* Buttons */
      .gsm-btn-primary, .gsm-btn-secondary, .gsm-btn-cancel, .gsm-btn-success, .gsm-btn-danger {
        display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        padding: 11px 20px; border-radius: 10px; font-weight: 600; font-size: 0.875rem;
        border: none; transition: all 0.2s; cursor: pointer;
      }
      .gsm-btn-primary { background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%); color: white; }
      .gsm-btn-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; }
      .gsm-btn-danger  { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: white; }
      .gsm-btn-primary:hover:not(:disabled), .gsm-btn-success:hover:not(:disabled), .gsm-btn-danger:hover:not(:disabled) {
        transform: translateY(-1px); box-shadow: 0 8px 16px -4px rgba(15, 23, 42, 0.25);
      }
      .gsm-btn-secondary, .gsm-btn-cancel { background: white; border: 1px solid #e2e8f0; color: #334155; }
      .gsm-btn-secondary:hover:not(:disabled), .gsm-btn-cancel:hover { background: #f1f5f9; }
      .gsm-btn-primary:disabled, .gsm-btn-success:disabled, .gsm-btn-danger:disabled, .gsm-btn-secondary:disabled {
        opacity: 0.5; cursor: not-allowed;
      }

      /* Alert */
      .gsm-alert {
        display: flex; align-items: center; gap: 12px; padding: 14px 18px;
        border-radius: 12px; font-size: 0.85rem; font-weight: 500;
      }
      .gsm-alert > i { font-size: 1.3rem; flex-shrink: 0; }
      .gsm-alert-danger { background: #fef2f2; color: #991b1b; border-left: 4px solid #ef4444; }
      .gsm-alert-warning { background: #fffbeb; color: #92400e; border-left: 4px solid #f59e0b; }
      .gsm-alert-info { background: #eff6ff; color: #1e40af; border-left: 4px solid #3b82f6; }

      /* Stats */
      .gsm-stat-card {
        display: flex; align-items: center; gap: 12px; background: white;
        border-radius: 14px; padding: 16px; border: 1px solid #e2e8f0; height: 100%;
      }
      .gsm-stat-icon {
        width: 42px; height: 42px; border-radius: 11px;
        display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
      }
      .gsm-icon-blue   { background: #dbeafe; color: #3b82f6; }
      .gsm-icon-green  { background: #d1fae5; color: #10b981; }
      .gsm-icon-amber  { background: #fef3c7; color: #d97706; }
      .gsm-icon-violet { background: #ede9fe; color: #8b5cf6; }
      .gsm-stat-label {
        font-size: 0.65rem; color: #64748b; font-weight: 700;
        text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px;
      }
      .gsm-stat-value { font-size: 1.35rem; font-weight: 700; color: #0f172a; line-height: 1; }

      /* Filters */
      .gsm-filters { background: white; border-radius: 14px; padding: 16px 20px; border: 1px solid #e2e8f0; }
      .gsm-search-wrapper { position: relative; display: flex; align-items: center; }
      .gsm-search-icon { position: absolute; left: 14px; color: #94a3b8; pointer-events: none; }
      .gsm-search-input, .gsm-select, .gsm-input {
        width: 100%; padding: 11px 14px; border: 1px solid #e2e8f0; border-radius: 10px;
        font-size: 0.875rem; background: #f8fafc; color: #0f172a; outline: none; font-family: inherit;
        transition: all 0.2s;
      }
      .gsm-search-input { padding-left: 42px; }
      .gsm-input { background: white; border-width: 2px; }
      .gsm-search-input:focus, .gsm-select:focus, .gsm-input:focus {
        background: white; border-color: #0ea5e9; box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
      }
      .gsm-input.is-invalid, .gsm-select.is-invalid { border-color: #ef4444; }
      .gsm-select:disabled { opacity: 0.6; cursor: not-allowed; }

      /* Tabs */
      .gsm-tabs { display: inline-flex; gap: 6px; background: white; padding: 6px; border-radius: 12px; border: 1px solid #e2e8f0; }
      .gsm-tab {
        border: none; background: none; padding: 9px 18px; border-radius: 8px;
        font-weight: 600; font-size: 0.85rem; color: #64748b; display: inline-flex; align-items: center; gap: 8px;
      }
      .gsm-tab.active { background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%); color: white; }

      /* Table */
      .gsm-table-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; }
      .gsm-table { font-size: 0.875rem; margin-bottom: 0; }
      .gsm-table thead th {
        background: #f8fafc; color: #475569; font-weight: 700; font-size: 0.7rem;
        text-transform: uppercase; letter-spacing: 0.05em; padding: 16px;
        border-bottom: 1px solid #e2e8f0; white-space: nowrap;
      }
      .gsm-table tbody td { padding: 14px 16px; vertical-align: middle; border-bottom: 1px solid #f1f5f9; }
      .gsm-table tbody tr:hover { background-color: #f8fafc; }
      .gsm-code {
        font-size: 0.78rem; color: #0e7490; background: #cffafe; padding: 4px 8px;
        border-radius: 5px; font-weight: 600; font-family: 'SF Mono', Menlo, Monaco, monospace;
      }
      .gsm-mono { font-family: 'SF Mono', Menlo, Monaco, monospace; font-size: 0.82rem; color: #334155; }
      .gsm-badge {
        display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px;
        border-radius: 6px; font-size: 0.72rem; font-weight: 700;
      }
      .gsm-badge-gray  { background: #f1f5f9; color: #475569; }
      .gsm-badge-green { background: #d1fae5; color: #059669; }
      .gsm-badge-amber { background: #fef3c7; color: #92400e; }
      .gsm-badge-red   { background: #fee2e2; color: #b91c1c; }
      .gsm-url { font-size: 0.78rem; color: #334155; word-break: break-all; }
      .gsm-progress { height: 6px; background: #e2e8f0; border-radius: 3px; overflow: hidden; min-width: 80px; }
      .gsm-progress > div { height: 100%; background: linear-gradient(90deg, #0ea5e9, #3b82f6); }
      .gsm-action-btn {
        width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e2e8f0;
        background: white; color: #64748b; display: inline-flex;
        align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s;
      }
      .gsm-action-edit:hover { background: #0ea5e9; border-color: #0ea5e9; color: white; }
      .gsm-action-delete:hover { background: #ef4444; border-color: #ef4444; color: white; }

      /* Loading / empty */
      .gsm-empty { padding: 60px 20px; text-align: center; color: #94a3b8; }
      .gsm-empty i { font-size: 36px; display: block; margin-bottom: 12px; }

      /* Modal */
      .gsm-modal .modal-content { border: none; border-radius: 16px; overflow: hidden; }
      .gsm-modal-header {
        display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; color: white;
      }
      .gsm-modal-header-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
      .gsm-modal-header-blue { background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%); }
      .gsm-modal-header-danger { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
      .gsm-modal-icon {
        width: 44px; height: 44px; border-radius: 10px; background: rgba(255,255,255,0.2);
        display: flex; align-items: center; justify-content: center; font-size: 20px;
      }
      .gsm-modal-title { font-weight: 700; margin: 0; }
      .gsm-modal-subtitle { color: rgba(255,255,255,0.85); font-size: 0.8rem; }
      .gsm-modal-close {
        background: rgba(255,255,255,0.1); border: none; color: white; width: 36px; height: 36px;
        border-radius: 10px; display: flex; align-items: center; justify-content: center; cursor: pointer;
      }
      .gsm-modal-close:hover { background: rgba(255,255,255,0.2); }
      .gsm-modal-body { padding: 24px; background: #f8fafc; max-height: 70vh; overflow-y: auto; }
      .gsm-modal-footer {
        padding: 16px 24px; background: white; border-top: 1px solid #e2e8f0;
        display: flex; justify-content: flex-end; gap: 10px;
      }
      .gsm-form-label { display: block; font-weight: 700; color: #0f172a; font-size: 0.82rem; margin-bottom: 8px; }
      .gsm-required { color: #ef4444; }
      .gsm-form-hint { margin-top: 6px; font-size: 0.72rem; color: #94a3b8; display: flex; align-items: center; gap: 4px; }
      .gsm-form-error { margin-top: 6px; font-size: 0.72rem; color: #ef4444; }

      @keyframes gsm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      .gsm-spin { display: inline-block; animation: gsm-spin 0.8s linear infinite; }
    `}</style>
  );
}
